import React, { useState, useEffect, useMemo } from 'react';
import { subscribeToAuthChanges, getCachedAppUser } from '../lib/authService';
import { auth, db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  X,
  CreditCard,
  CheckCircle2,
  Lock,
  Download,
  ShieldCheck,
  FileText,
  Sparkles,
  ArrowRight,
  Globe,
  Building2,
  Phone,
  AlertCircle,
  Copy,
  Check,
  Wallet,
  DollarSign,
  Upload,
  Clock,
  Image as ImageIcon
} from 'lucide-react';
import { CartItem, Coupon, Currency, Order, DynamicPaymentGateway } from '../types';
import { saveOrderToFirestore, saveManualPaymentSubmission } from '../lib/ordersService';
import { logActivityToFirestore } from '../lib/activityLogsService';
import { DEFAULT_DYNAMIC_GATEWAYS } from '../lib/paymentGatewayService';
import { getInvoiceSettingsFromFirestore, InvoiceSettings } from '../lib/invoiceSettings';
import { BRAND_LOGOS } from '../data/brandLogos';
import { PaymentBrandBadge } from './PaymentBrandBadge';
import { COUNTRIES_WITH_PHONE, validatePhoneNumberForCountry } from '../lib/countryPhoneData';
import { PhoneCountryInput } from './checkout/PhoneCountryInput';
import { AddressAutocompleteInput } from './checkout/AddressAutocompleteInput';
import { updateUserProfileInFirestore } from '../lib/authService';

// Helper to get local date time string YYYY-MM-DDTHH:mm
const getLocalDatetimeNow = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${mins}`;
};

import { Product } from "../types";

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  appliedCoupon: Coupon | null;
  currency: Currency;
  onOrderComplete: (order: Order) => void;
  darkMode: boolean;
  dynamicGateways?: DynamicPaymentGateway[];
  onNavigateProduct?: (product: Product) => void;
}

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  cartItems,
  appliedCoupon,
  currency,
  onOrderComplete,
  darkMode,
  dynamicGateways = DEFAULT_DYNAMIC_GATEWAYS,
  onNavigateProduct,
}) => {
  const gatewaysToUse = dynamicGateways || DEFAULT_DYNAMIC_GATEWAYS;

  const activeGateways = gatewaysToUse
    .filter(gw => gw.enabled !== false)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  const [selectedGatewayId, setSelectedGatewayId] = useState<string>(activeGateways[0]?.id || 'stripe');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerCountry, setCustomerCountry] = useState('United States');
  const [customerAddress, setCustomerAddress] = useState('');

  // Card Inputs
  const [cardNumber, setCardNumber] = useState('4242 •••• •••• 4242');
  const [expiry, setExpiry] = useState('12/28');
  const [cvc, setCvc] = useState('888');

  // Manual Payment Form State
  const [manualTrxId, setManualTrxId] = useState('');
  const [manualAmount, setManualAmount] = useState('0.00');
  const [manualDateTime, setManualDateTime] = useState(getLocalDatetimeNow());
  const [manualSenderAccount, setManualSenderAccount] = useState('');
  const [manualScreenshotUrl, setManualScreenshotUrl] = useState('');
  const [manualNote, setManualNote] = useState('');

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [completedOrder, setCompletedOrder] = useState<Order | null>(null);
  const [copiedText, setCopiedText] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(true);
  const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettings | null>(null);
  const [showQrZoom, setShowQrZoom] = useState(false);

  // Synchronize customer profile to Firestore and LocalStorage
  const saveCustomerDetailsToProfile = async () => {
    const profilePayload = {
      name: customerName.trim(),
      email: customerEmail.trim(),
      phone: customerPhone.trim(),
      country: customerCountry.trim(),
      address: customerAddress.trim()
    };

    try {
      localStorage.setItem('rh_customer_profile', JSON.stringify(profilePayload));
    } catch (e) {}

    const uid = auth.currentUser?.uid || getCachedAppUser()?.uid;
    if (uid) {
      try {
        await updateUserProfileInFirestore(uid, profilePayload);
        console.log('[Checkout] Synced customer profile to Firestore for UID:', uid);
      } catch (syncErr) {
        console.warn('[Checkout] Background profile sync warning:', syncErr);
      }
    }
  };

  useEffect(() => {
    getInvoiceSettingsFromFirestore().then((settings) => {
      if (settings) setInvoiceSettings(settings);
    });

    // Check localStorage profile first
    try {
      const storedStr = localStorage.getItem('rh_customer_profile');
      if (storedStr) {
        const stored = JSON.parse(storedStr);
        if (stored.name) setCustomerName(stored.name);
        if (stored.email) setCustomerEmail(stored.email);
        if (stored.phone) setCustomerPhone(stored.phone);
        if (stored.country) setCustomerCountry(stored.country);
        if (stored.address) setCustomerAddress(stored.address);
      }
    } catch (e) {}

    const unsub = subscribeToAuthChanges((u) => {
      if (u) {
        if (u.name) setCustomerName(u.name);
        if (u.email) setCustomerEmail(u.email);
        if ((u as any).phone) setCustomerPhone((u as any).phone);
        if ((u as any).country) setCustomerCountry((u as any).country);
        if ((u as any).address) setCustomerAddress((u as any).address);
        setIsEmailVerified(u.emailVerified !== false);
      }
    });

    // Also fetch latest profile from Firestore for authenticated customer
    const currentUid = auth.currentUser?.uid || getCachedAppUser()?.uid;
    if (currentUid) {
      getDoc(doc(db, 'users', currentUid)).then((snap) => {
        if (snap.exists()) {
          const d = snap.data();
          if (d.name) setCustomerName(d.name);
          if (d.email) setCustomerEmail(d.email);
          if (d.phone) setCustomerPhone(d.phone);
          if (d.country) setCustomerCountry(d.country);
          if (d.address) setCustomerAddress(d.address);
        }
      }).catch(() => {});
    }

    return () => unsub();
  }, []);

  const phoneValidationLive = useMemo(() => {
    if (!customerPhone.trim()) return { isValid: true };
    return validatePhoneNumberForCountry(customerPhone, customerCountry);
  }, [customerPhone, customerCountry]);

  const currencySymbols: Record<Currency, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    };

  const exchangeRates: Record<Currency, number> = {
    USD: 1,
    EUR: 0.92,
    GBP: 0.78,
    };

  const symbol = currencySymbols[currency];
  const rate = exchangeRates[currency];

  const subtotal = cartItems.reduce((acc, item) => acc + item.product.price * item.quantity, 0);
  const discountAmount = appliedCoupon ? (subtotal * appliedCoupon.discountPercent) / 100 : 0;
  const grandTotal = Math.max(0, subtotal - discountAmount);

  const selectedGateway: DynamicPaymentGateway = activeGateways.find(g => g.id === selectedGatewayId) || activeGateways[0] || {
    id: 'stripe',
    name: 'Stripe Credit Card',
    description: 'Accept credit card payments.',
    enabled: true,
    testMode: true,
    instructions: 'Pay securely using credit card.',
    displayOrder: 1,
  };

  // Automatically calculate gateway processing fee
  const feePercent = selectedGateway.processingFeePercent || 0;
  const feeFixed = selectedGateway.processingFeeFixed || 0;
  const processingFeeUSD = (grandTotal * (feePercent / 100)) + feeFixed;
  const processingFeeInCurrency = processingFeeUSD * rate;

  const finalPayableUSD = grandTotal + processingFeeUSD;
  const totalAmountInCurrency = (finalPayableUSD * rate).toFixed(2);

  useEffect(() => {
    setManualAmount(totalAmountInCurrency);
  }, [selectedGatewayId, currency, totalAmountInCurrency]);

  if (!isOpen) return null;

  const isNowpayments = selectedGateway.id.toLowerCase() === 'nowpayments';

  const isManualGateway = (gw: DynamicPaymentGateway) => {
    if (gw.type === 'manual') return true;
    if (gw.type === 'automatic') return false;
    const id = (gw.id || '').toLowerCase();
    const strictAutoGateways = [
      'nowpayments',
      'stripe',
      'paypal',
      'sslcommerz',
      'coinbase',
      'coingate',
      'bitpay',
      'helaket'
    ];
    if (strictAutoGateways.includes(id)) {
      return false;
    }
    // All custom or crypto/bank transfer gateways created by admin default to manual/custom payment instructions & verification
    return true;
  };

  const isPaypal = selectedGateway.id.toLowerCase() === 'paypal';
  const isPaypalConfigured = isPaypal && !!selectedGateway.clientId && selectedGateway.clientId !== 'client_id_live_rheditography_sample' && !selectedGateway.clientId.includes('sample');

  const getGatewayIcon = (logoIcon?: string) => {
    switch (logoIcon) {
      case 'Globe': return Globe;
      case 'Phone': return Phone;
      case 'Building2': return Building2;
      case 'FileText': return FileText;
      case 'ShieldCheck': return ShieldCheck;
      case 'Wallet': return Wallet;
      case 'DollarSign': return DollarSign;
      case 'CreditCard':
      default: return CreditCard;
    }
  };

  const renderGatewayLogoIcon = (logoIcon?: string, isSelected?: boolean, gwId?: string, gwName?: string) => {
    let logoUrl = '';

    if (logoIcon && (logoIcon.startsWith('data:') || logoIcon.startsWith('http') || logoIcon.startsWith('/'))) {
      logoUrl = logoIcon;
    } else {
      const searchTerm = (logoIcon || gwId || gwName || '').toLowerCase();
      const matchedBrand = BRAND_LOGOS.find(
        (b) =>
          b.id.toLowerCase() === searchTerm ||
          b.name.toLowerCase() === searchTerm ||
          b.brandName.toLowerCase() === searchTerm ||
          (gwId && (b.id.toLowerCase() === gwId.toLowerCase() || gwId.toLowerCase().includes(b.id.toLowerCase()) || b.id.toLowerCase().includes(gwId.toLowerCase()))) ||
          (gwName && b.brandName.toLowerCase().includes(gwName.toLowerCase())) ||
          (searchTerm && b.keywords?.some((k) => k.toLowerCase() === searchTerm))
      );
      if (matchedBrand) {
        logoUrl = matchedBrand.logoUrl;
      }
    }

    if (logoUrl) {
      return (
        <div className="w-11 h-11 rounded-2xl bg-slate-900 border border-white/20 p-1.5 flex items-center justify-center shrink-0 shadow-lg">
          <img
            src={logoUrl}
            alt="Logo"
            className="w-full h-full object-contain rounded-xl"
            onError={(e) => {
              (e.target as HTMLElement).style.display = 'none';
            }}
          />
        </div>
      );
    }

    const Icon = getGatewayIcon(logoIcon);
    return (
      <div className={`w-11 h-11 rounded-2xl border p-2 flex items-center justify-center shrink-0 shadow-lg ${isSelected ? 'bg-cyan-500/20 border-cyan-400 text-cyan-300' : 'bg-slate-900 border-slate-700 text-slate-400'}`}>
        <Icon className="w-6 h-6" />
      </div>
    );
  };

  const handleCopyNumber = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2000);
  };

  const handleScreenshotUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('Screenshot file size exceeds 5MB limit.');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        // Compress image before setting to state to avoid Firestore 1MB document limit
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.6);
          setManualScreenshotUrl(compressedBase64);
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePay = async () => {
    setErrorMessage('');

    if (!customerName.trim() || customerName.trim().length < 2) {
      setErrorMessage('Please enter a valid Full Name (at least 2 characters).');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!customerEmail.trim() || !emailRegex.test(customerEmail.trim())) {
      setErrorMessage('Please enter a valid Email Address (e.g., name@domain.com).');
      return;
    }
    if (!customerCountry.trim()) {
      setErrorMessage('Please select your Country.');
      return;
    }
    const phoneValidation = validatePhoneNumberForCountry(customerPhone, customerCountry);
    if (!phoneValidation.isValid) {
      setErrorMessage(phoneValidation.errorMessage || 'Please enter a valid Phone Number for your selected country.');
      return;
    }
    if (phoneValidation.cleanedFullNumber) {
      setCustomerPhone(phoneValidation.cleanedFullNumber);
    }
    if (!customerAddress.trim() || customerAddress.trim().length < 5) {
      setErrorMessage('Please enter a valid Address / City (at least 5 characters).');
      return;
    }

    // PayPal Check
    if (isPaypal && !isPaypalConfigured) {
      setErrorMessage('PayPal is not configured yet. Live payments are currently unavailable.');
      return;
    }

    const isManual = isManualGateway(selectedGateway);

    // Validation for Manual Payments
    if (isManual) {
      if (!manualTrxId.trim() || manualTrxId.trim().length < 4) {
        setErrorMessage(`Please enter a valid Transaction ID (TrxID) for ${selectedGateway.name} (at least 4 characters).`);
        return;
      }
      if (!manualAmount || isNaN(Number(manualAmount)) || parseFloat(manualAmount) <= 0) {
        setErrorMessage('Please enter a valid Payment Amount.');
        return;
      }
      if (!manualDateTime.trim()) {
        setErrorMessage('Please specify the Payment Date & Time.');
        return;
      }
      if (!manualSenderAccount.trim() || manualSenderAccount.trim().length < 4) {
        setErrorMessage(`Please enter a valid Sender Number or Account Number for ${selectedGateway.name} (at least 4 characters).`);
        return;
      }
    }

    setIsProcessing(true);

    let orderId = `RH-${Math.floor(100000 + Math.random() * 900000)}`;
    let invoiceNum = `INV-2026-${Math.floor(10000 + Math.random() * 90000)}`;
    
    // Prevent duplicate pending orders if customer retries the same checkout
    try {
      const existingDraftStr = localStorage.getItem('rh_active_gateway_order');
      if (existingDraftStr) {
        const existingDraft = JSON.parse(existingDraftStr);
        // Reuse same order ID if the total amount hasn't changed
        if (existingDraft && existingDraft.id && existingDraft.total === finalPayableUSD) {
          orderId = existingDraft.id;
          if (existingDraft.invoiceNumber) {
            invoiceNum = existingDraft.invoiceNumber;
          }
        }
      }
    } catch(e) {}

    // AUTOMATED API GATEWAYS (NOWPayments, Bybit, Binance Pay, Coinbase, Helaket, BitPay, CoinGate, PayPal, Stripe, SSLCommerz)
    if (!isManual) {
      console.log(`[API Checkout] Initiating instant automated API checkout for ${selectedGateway.name} (${selectedGateway.id})`);

      const newOrder: Order = {
        id: orderId,
        invoiceNumber: invoiceNum,
        date: new Date().toISOString().split('T')[0],
        items: cartItems,
        subtotal,
        discountAmount,
        processingFee: processingFeeUSD,
        total: finalPayableUSD,
        status: 'Awaiting Payment',
        paymentMethod: selectedGateway.name,
        paymentStatus: 'Awaiting Payment',
        transactionId: `${selectedGateway.id.toUpperCase().slice(0, 4)}-${Math.random().toString(36).substring(2, 9).toUpperCase()}`,
        userId: auth.currentUser?.uid || getCachedAppUser()?.uid || '',
        customerName,
        customerEmail,
        customerPhone,
        customerCountry,
        customerAddress,
        customerNotes: `Automated Checkout via ${selectedGateway.name} Gateway`,
        invoiceUrl: `/api/download/${orderId}?type=invoice`,
      };

      const id = selectedGateway.id.toLowerCase();
      const apiEndpoint = id === 'nowpayments' ? '/api/nowpayments/create-invoice' : '/api/gateway/create-invoice';

      try {
        // Wait for gateway initialization BEFORE saving the order to prevent duplicates/invalid orders
        const res = await fetch(apiEndpoint, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            gatewayId: selectedGateway.id,
            amount: totalAmountInCurrency,
            currency: currency,
            orderId: orderId,
            customerEmail,
            customerName,
            items: cartItems
          })
        });

        const contentType = res.headers.get('content-type') || '';
        let data: any = {};
        if (contentType.includes('application/json')) {
          data = await res.json();
        }

        if (res.ok && data.success && (data.invoice_url || data.checkout_url)) {
          const invoiceUrl = data.invoice_url || data.checkout_url;
          console.log('[API Checkout] Dynamic invoice created:', invoiceUrl);

          // ONLY NOW we save the order and log it because initialization was successful
          try {
            localStorage.setItem('rh_active_nowpayments_order', JSON.stringify(newOrder));
            localStorage.setItem('rh_active_gateway_order', JSON.stringify(newOrder));
          } catch (e) {}

          // Persist customer profile details to dashboard and Firestore
          await saveCustomerDetailsToProfile().catch(e => console.warn('Profile sync notice:', e));

          // NOTE: Order is saved here in Pending state BEFORE payment is complete. 
          // This is standard e-commerce practice so that if the customer drops off at the payment page,
          // the admin can see the abandoned cart / pending order. 
          await saveOrderToFirestore(newOrder).catch(err => console.warn('Background order save error:', err));
          await logActivityToFirestore({
            type: 'Order Created',
            orderId: newOrder.id,
            customerName: newOrder.customerName,
            amount: newOrder.total,
            details: `Order ${newOrder.id} initiated via ${selectedGateway.name} API Gateway`
          }).catch(err => console.warn('Background activity log error:', err));

          console.log('[API Checkout] Redirecting to:', invoiceUrl);
          setIsProcessing(false);
          window.location.href = invoiceUrl;
          return;
        } else {
          // Gateway failed to initialize (e.g. missing API key, invalid credentials)
          console.error('[API Checkout] Gateway initialization failed:', data.message || 'Unknown error');
          setIsProcessing(false);
          setErrorMessage(data.message || 'Payment gateway is currently unavailable. Please choose another payment method or try again later.');
          return; // DO NOT CREATE ORDER
        }
      } catch (err) {
        console.error('[API Checkout] Network or server error during gateway initialization:', err);
        setIsProcessing(false);
        setErrorMessage('Failed to connect to the payment gateway. Please try again later.');
        return; // DO NOT CREATE ORDER
      }
    }

    if (isManual) {
      // ENTERPRISE MANUAL PAYMENT WORKFLOW
      const submittedAmountNum = parseFloat(manualAmount) || grandTotal * rate;
      const productSummary = cartItems.map(i => `${i.product.title} (.${i.selectedFormat})`).join(', ');

      const newOrder: Order = {
        id: orderId,
        invoiceNumber: invoiceNum,
        date: new Date().toISOString().split('T')[0],
        items: cartItems,
        subtotal,
        discountAmount,
        processingFee: processingFeeUSD,
        total: finalPayableUSD,
        status: 'Pending Payment Verification',
        paymentMethod: selectedGateway.name,
        paymentStatus: 'Pending Verification',
        transactionId: manualTrxId.trim(),
        downloadToken: '', // Downloads remain LOCKED!
        userId: auth.currentUser?.uid || getCachedAppUser()?.uid || '',
        customerName: customerName || '',
        customerEmail: customerEmail || '',
        customerPhone: customerPhone || '',
        customerNotes: `Manual Payment (${selectedGateway.name}). Sender: ${manualSenderAccount.trim()}, TrxID: ${manualTrxId.trim()}`,
        invoiceUrl: `/api/download/${orderId}?type=invoice`,
        manualPaymentDetails: {
          transactionId: manualTrxId.trim(),
          paymentAmount: submittedAmountNum,
          paymentDateTime: manualDateTime || new Date().toISOString(),
          senderAccount: manualSenderAccount.trim(),
          screenshotUrl: manualScreenshotUrl || '',
          paymentNote: manualNote || '',
          submittedAt: new Date().toISOString(),
          rejectionReason: ''
        }
      };

      try {
        console.log(`REAL CHECKOUT STARTED for Order #${orderId}`);

        // Persist customer profile details to dashboard and Firestore
        await saveCustomerDetailsToProfile().catch(e => console.warn('Profile sync notice:', e));

        // 1. Save to main orders collection
        await saveOrderToFirestore(newOrder);

        // 2. Save directly to manual_payment_submissions collection
        await saveManualPaymentSubmission({
          id: orderId,
          orderId: orderId,
          customerName: customerName || '',
          customerEmail: customerEmail || '',
          gatewayName: selectedGateway.name || 'Manual Payment',
          transactionId: manualTrxId.trim(),
          senderAccount: manualSenderAccount.trim(),
          amount: submittedAmountNum,
          status: 'pending',
          createdAt: new Date().toISOString(),
          receiptImage: manualScreenshotUrl || '',
          productDetails: productSummary || '',
          note: manualNote || '',
          rejectionReason: ''
        });

        console.log(`REAL CHECKOUT FIRESTORE WRITE SUCCESS for Order #${orderId}`);

        // 3. Log Activity
        await logActivityToFirestore({
          type: 'Manual Payment Submitted',
          orderId: newOrder.id,
          customerName: newOrder.customerName,
          amount: newOrder.total,
          details: `Manual Payment #${newOrder.id} submitted via ${selectedGateway.name} (TrxID: ${manualTrxId.trim()}, Sender: ${manualSenderAccount.trim()})`
        });

        // 4. Send Instant Admin Email Notification
        try {
          await fetch('/api/auth/send-email', {
            method: 'POST',
      credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: 'support@rheditography.net',
              subject: 'New Manual Payment Submission',
              html: `
                <div style="font-family: Arial, sans-serif; background: #0f172a; color: #ffffff; padding: 30px; border-radius: 16px;">
                  <h2 style="color: #fbbf24; margin-top: 0;">🚨 New Manual Payment Received</h2>
                  <p>A new manual payment submission requires administrative verification:</p>
                  <div style="background: #1e293b; padding: 20px; border-radius: 12px; margin: 20px 0; border-left: 4px solid #f59e0b;">
                    <p style="margin: 6px 0;"><strong>Order ID:</strong> #${orderId}</p>
                    <p style="margin: 6px 0;"><strong>Customer Name:</strong> ${customerName}</p>
                    <p style="margin: 6px 0;"><strong>Customer Email:</strong> ${customerEmail}</p>
                    <p style="margin: 6px 0;"><strong>Gateway:</strong> ${selectedGateway.name}</p>
                    <p style="margin: 6px 0;"><strong>Amount:</strong> ${symbol}${submittedAmountNum}</p>
                    <p style="margin: 6px 0;"><strong>Transaction ID:</strong> ${manualTrxId.trim()}</p>
                    <p style="margin: 6px 0;"><strong>Sender Account:</strong> ${manualSenderAccount.trim()}</p>
                  </div>
                  <p>Log in to your Admin Dashboard under <strong>Manual Verification</strong> to approve or reject this submission.</p>
                </div>
              `
            })
          });
        } catch (adminEmailErr) {
          console.warn('Admin email dispatch alert warning:', adminEmailErr);
        }

        // 5. Send Customer Email Confirmation
        try {
          const itemsListHtml = cartItems.map(it => {
            const pTitle = it.product?.title || 'Digital Template';
            const pPrice = Number(it.product?.price || 0);
            return `<li style="margin-bottom: 4px;"><strong>${pTitle}</strong> (${it.selectedFormat || 'PSD'}) x${it.quantity || 1} - ${symbol}${pPrice.toFixed(2)}</li>`;
          }).join('');

          await fetch('/api/auth/send-email', {
            method: 'POST',
      credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: customerEmail,
              subject: `[RH Editography] Manual Payment Received for Verification - Order #${orderId}`,
              html: `
                <div style="font-family: Arial, sans-serif; background: #0f172a; color: #ffffff; padding: 30px; border-radius: 16px;">
                  <h2 style="color: #fbbf24; margin-top: 0;">RH Editography - Payment Verification Pending</h2>
                  <p>Hello <strong>${customerName}</strong>,</p>
                  <p>We have received your manual payment submission for order <strong>#${orderId}</strong> via <strong>${selectedGateway.name}</strong>.</p>
                  <div style="background: #1e293b; padding: 15px; border-radius: 8px; margin: 20px 0;">
                    <p><strong>Transaction ID:</strong> ${manualTrxId.trim()}</p>
                    <p><strong>Sender Account:</strong> ${manualSenderAccount.trim()}</p>
                    <p><strong>Amount Submitted:</strong> ${symbol}${submittedAmountNum}</p>
                    <p><strong>Payment Status:</strong> Pending Verification</p>
                  </div>
                  <div style="background: #1e293b; padding: 15px; border-radius: 8px; margin: 20px 0;">
                    <div style="font-size: 13px; font-weight: bold; color: #94a3b8; text-transform: uppercase; margin-bottom: 8px;">Purchased Templates:</div>
                    <ul style="font-size: 13px; color: #cbd5e1; margin: 0; padding-left: 20px;">
                      ${itemsListHtml}
                    </ul>
                  </div>
                  <p>Our administration team is verifying your payment details. Once approved, your digital downloads will unlock automatically in your Customer Dashboard.</p>
                </div>
              `
            })
          });
        } catch (emailErr) {
          console.warn('Customer email dispatch notice warning:', emailErr);
        }

        setIsProcessing(false);
        setCompletedOrder(newOrder);
        onOrderComplete(newOrder);

      } catch (err) {
        console.error('REAL CHECKOUT FIRESTORE WRITE FAILED:', err);
        setIsProcessing(false);
        setErrorMessage('Failed to submit payment details to Firestore. Please check your network connection and try again.');
      }

    } else {
      // INSTANT AUTOMATED ONLINE PAYMENT (e.g. Stripe, SSLCommerz)
      const token = `DL-TOKEN-${Math.random().toString(36).substr(2, 12).toUpperCase()}`;

      const newOrder: Order = {
        id: orderId,
        invoiceNumber: invoiceNum,
        date: new Date().toISOString().split('T')[0],
        items: cartItems,
        subtotal,
        discountAmount,
        total: grandTotal,
        status: 'Pending',
        paymentMethod: selectedGateway.name,
        paymentStatus: 'Pending',
        transactionId: `TXN-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
        downloadToken: token,
        customerName,
        customerEmail,
        customerPhone,
        customerCountry,
        customerAddress,
        customerNotes: 'Automated credit card verification completed.',
        invoiceUrl: `/api/download/${orderId}?type=invoice`,
      };

      try {
        // Persist customer profile details to dashboard and Firestore
        await saveCustomerDetailsToProfile().catch(e => console.warn('Profile sync notice:', e));

        await saveOrderToFirestore(newOrder);
        await logActivityToFirestore({
          type: 'Payment Completed',
          orderId: newOrder.id,
          customerName: newOrder.customerName,
          amount: newOrder.total,
          details: `Order ${newOrder.id} (${selectedGateway.name}) initialized as Pending`
        });

        setIsProcessing(false);
        setCompletedOrder(newOrder);
        onOrderComplete(newOrder);

      } catch (err) {
        console.error('Checkout error:', err);
        setIsProcessing(false);
        setErrorMessage('Payment execution failed. Please check your details or try another method.');
      }
    }
  };

  // Compute current stepper active step
  let currentStep = 2; // Default: Checkout
  if (completedOrder) {
    if (completedOrder.status === 'Completed') {
      currentStep = 5; // Download
    } else {
      currentStep = 4; // Verification
    }
  } else if (isProcessing) {
    currentStep = 3; // Payment
  }

  const isBankGateway = selectedGateway.id.toLowerCase().includes('bank');
  const isSslcommerz = selectedGateway.id.toLowerCase() === 'sslcommerz';
  const isManualPayment = ['manual'].includes(selectedGateway.id.toLowerCase());

  const getDynamicButtonText = () => {
    const amt = `${symbol}${totalAmountInCurrency}`;

    // 1. If admin configured custom buttonText on this gateway (e.g. "Submit Order"), ALWAYS honor it first!
    if (selectedGateway?.buttonText && selectedGateway.buttonText.trim()) {
      return `${selectedGateway.buttonText.trim()} (${amt})`;
    }

    const id = selectedGateway.id.toLowerCase();
    if (id === 'stripe') return `Pay Securely (${amt})`;
    if (id === 'paypal') return `Continue to PayPal (${amt})`;
    if (id === 'nowpayments') return `Pay with Crypto (${amt})`;
    if (id === 'bybit') return `Pay via Bybit Pay (${amt})`;
    if (id.includes('binance')) return `Pay via Binance Pay (${amt})`;
    if (id === 'coinbase') return `Pay via Coinbase Commerce (${amt})`;
    if (id === 'helaket') return `Pay via Helaket (${amt})`;
    if (id === 'bitpay') return `Pay with BitPay (${amt})`;
    if (id === 'coingate') return `Pay via CoinGate (${amt})`;
    if (id === 'sslcommerz') return `Pay with SSLCommerz (${amt})`;
    if (isBankGateway) return `Submit Transfer (${amt})`;
    if (isManualPayment) return `Submit Payment (${amt})`;
    if (isManualGateway(selectedGateway)) return `Submit Verification (${amt})`;
    return `Pay ${amt}`;
  };

  return (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center p-2.5 sm:p-4 bg-slate-950/90 backdrop-blur-md animate-fade-in overflow-y-auto">
      {/* 1px Glowing Gradient Border Container (Exact 1px padding everywhere) */}
      <div 
        style={{ padding: '1px' }}
        className="relative w-full max-w-5xl my-auto rounded-[20px] sm:rounded-[24px] bg-gradient-to-br from-[#0070f3] via-[#8b5cf6] to-[#06b6d4] shadow-[0_0_30px_rgba(139,92,246,0.25)] shrink-0"
      >
        {/* Inner Card (Concentric border radius 19px mobile / 23px desktop, 100% width) */}
        <div 
          style={{ width: '100%' }}
          className="relative w-full max-h-[92vh] sm:max-h-[90vh] flex flex-col rounded-[19px] sm:rounded-[23px] bg-[#0b1120] shadow-2xl text-white overflow-hidden"
        >
          {/* Top subtle blue/purple line - internal accent */}
          <div className="absolute top-0 left-8 w-20 h-[3px] bg-gradient-to-r from-blue-500 to-purple-500 rounded-b-full z-10"></div>
          
        {/* Modal Header (Fixed at top of modal) */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-white/10 bg-slate-900/90 z-20 ">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors text-xs font-bold border border-slate-700 cursor-pointer"
            >
              <ArrowRight className="w-3.5 h-3.5 rotate-180" />
              <span>Back to Cart</span>
            </button>
            <div className="hidden sm:flex items-center gap-2 border-l border-slate-800 pl-3">
              <div className="p-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                <ShieldCheck className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-extrabold text-sm tracking-tight text-white flex items-center gap-2">
                  <span>Enterprise Secure Checkout</span>
                  <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold">
                    256-Bit SSL
                  </span>
                </h3>
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800/80 hover:bg-rose-500/80 text-slate-400 hover:text-white transition-colors text-xs font-bold border border-slate-700 cursor-pointer"
            title="Close Checkout"
          >
            <X className="w-4 h-4" />
            <span className="hidden sm:inline">Close</span>
          </button>
        </div>

        {/* CHECKOUT PROGRESS TIMELINE (Fixed Stepper) */}
        <div className="flex-shrink-0 px-3 sm:px-6 py-2.5 bg-slate-900/60 border-b border-white/5 flex items-center justify-between gap-1.5 sm:gap-3 text-[11px] font-bold text-slate-400 overflow-x-auto no-scrollbar whitespace-nowrap">
          {/* Step 1: Cart */}
          <div className={`flex items-center gap-1.5 shrink-0 whitespace-nowrap ${currentStep >= 1 ? 'text-cyan-400' : 'text-slate-500'}`}>
            <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 flex items-center justify-center text-[10px] font-mono shrink-0">✓</span>
            <span className="shrink-0">Cart</span>
          </div>
          <span className="text-slate-700 shrink-0">→</span>

          {/* Step 2: Checkout */}
          <div className={`flex items-center gap-1.5 shrink-0 whitespace-nowrap ${currentStep === 2 ? 'text-cyan-400 font-extrabold' : currentStep > 2 ? 'text-cyan-400' : 'text-slate-500'}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono shrink-0 ${currentStep === 2 ? 'bg-cyan-500 text-slate-950 font-black' : currentStep > 2 ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40' : 'bg-slate-800 text-slate-500 border border-slate-700'}`}>2</span>
            <span className="shrink-0">Checkout</span>
          </div>
          <span className="text-slate-700 shrink-0">→</span>

          {/* Step 3: Payment */}
          <div className={`flex items-center gap-1.5 shrink-0 whitespace-nowrap ${currentStep === 3 ? 'text-cyan-400 font-extrabold' : currentStep > 3 ? 'text-cyan-400' : 'text-slate-500'}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono shrink-0 ${currentStep === 3 ? 'bg-cyan-500 text-slate-950 font-black' : currentStep > 3 ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40' : 'bg-slate-800 text-slate-500 border border-slate-700'}`}>3</span>
            <span className="shrink-0">Payment</span>
          </div>
          <span className="text-slate-700 shrink-0">→</span>

          {/* Step 4: Verification */}
          <div className={`flex items-center gap-1.5 shrink-0 whitespace-nowrap ${currentStep === 4 ? 'text-amber-400 font-extrabold' : currentStep > 4 ? 'text-cyan-400' : 'text-slate-500'}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono shrink-0 ${currentStep === 4 ? 'bg-amber-500 text-slate-950 font-black' : currentStep > 4 ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40' : 'bg-slate-800 text-slate-500 border border-slate-700'}`}>4</span>
            <span className="shrink-0">Verification</span>
          </div>
          <span className="text-slate-700 shrink-0">→</span>

          {/* Step 5: Download */}
          <div className={`flex items-center gap-1.5 shrink-0 whitespace-nowrap ${currentStep === 5 ? 'text-emerald-400 font-extrabold' : 'text-slate-500'}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono shrink-0 ${currentStep === 5 ? 'bg-emerald-500 text-slate-950 font-black' : 'bg-slate-800 text-slate-500 border border-slate-700'}`}>5</span>
            <span className="shrink-0">Download</span>
          </div>
        </div>

        {/* MODAL SCROLLABLE BODY */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar p-3 sm:p-6 max-w-full">
          
          {/* COMPLETED ORDER VIEW */}
          {completedOrder ? (
            <div className="p-4 sm:p-6 text-center space-y-6">
              {completedOrder.status === 'Pending Payment Verification' ? (
                <div className="space-y-4 max-w-lg mx-auto">
                  <div className="w-16 h-16 rounded-3xl bg-amber-500/20 text-amber-400 border border-amber-500/40 mx-auto flex items-center justify-center shadow-2xl shadow-amber-500/20 animate-pulse">
                    <Clock className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black text-white">Payment Verification Pending</h3>
                  <p className="text-xs text-amber-300 bg-amber-950/60 p-3.5 rounded-2xl border border-amber-500/40 leading-relaxed">
                    Your manual payment details have been submitted to our billing team for review. Order ID: <strong className="font-mono text-white">#{completedOrder.id}</strong>.
                  </p>

                  <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 text-left space-y-2 text-xs">
                    <div className="flex justify-between items-center border-b border-white/5 pb-2">
                      <span className="text-slate-400">Payment Gateway:</span>
                      <PaymentBrandBadge paymentMethod={completedOrder.paymentMethod} size="sm" />
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-slate-400">Transaction ID:</span>
                      <span className="font-mono font-bold text-white break-all">{completedOrder.manualPaymentDetails?.transactionId}</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-slate-400">Sender Account:</span>
                      <span className="font-bold text-white break-all">{completedOrder.manualPaymentDetails?.senderAccount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Amount Paid:</span>
                      <span className="font-extrabold text-emerald-400">{symbol}{completedOrder.manualPaymentDetails?.paymentAmount.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-900/95 border border-slate-800 text-[11px] text-slate-400 flex items-center gap-2">
                    <Lock className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span>Digital downloads remain locked until administrator approval. You can track your payment status in your Customer Dashboard.</span>
                  </div>

                  <button
                    onClick={onClose}
                    className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 font-extrabold text-white text-xs shadow-xl hover:scale-105 transition-transform cursor-pointer"
                  >
                    Close & View Dashboard
                  </button>
                </div>
              ) : (
                <div className="space-y-4 max-w-lg mx-auto">
                  <div className="w-16 h-16 rounded-3xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 mx-auto flex items-center justify-center shadow-2xl shadow-emerald-500/20">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black text-white">Payment Successful!</h3>
                  <p className="text-xs text-slate-300">
                    Thank you for your order <strong className="text-cyan-400">#{completedOrder.id}</strong>. Your single-use digital download token is active.
                  </p>
                  <button
                    onClick={onClose}
                    className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-cyan-500 font-extrabold text-white text-xs shadow-xl hover:scale-105 transition-transform cursor-pointer"
                  >
                    Access Downloads in Dashboard
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* FORM & GATEWAYS VIEW */
            <div className="grid md:grid-cols-12 gap-6 max-w-full">
              
              {/* Left Column: Gateways & Payment Form */}
              <div className="md:col-span-7 space-y-4 max-w-full min-w-0">
                
                {errorMessage && (
                  <div className="p-3.5 rounded-2xl bg-rose-950/80 border border-rose-500/60 text-rose-200 text-xs font-bold flex items-center gap-2 shadow-lg animate-pulse">
                    <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                {/* Gateway Selection Buttons */}
                <div>
                  <label className="block text-xs font-extrabold uppercase tracking-wider text-slate-400 mb-2.5">
                    Select Payment Gateway
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {activeGateways.map((gw) => {
                      const isSelected = gw.id === selectedGatewayId;
                      return (
                        <button
                          key={gw.id}
                          type="button"
                          onClick={() => {
                            setSelectedGatewayId(gw.id);
                            setErrorMessage('');
                          }}
                          className={`p-3 sm:p-3.5 rounded-2xl border text-left transition-all relative overflow-hidden flex items-center justify-between gap-3 cursor-pointer ${
                            isSelected
                              ? 'bg-gradient-to-br from-blue-900/70 via-slate-900 to-cyan-950/90 border-cyan-400 text-white shadow-xl shadow-cyan-500/20 ring-1 ring-cyan-400/50'
                              : 'bg-slate-900/95 hover:bg-slate-900 border-slate-800 text-slate-300'
                          }`}
                        >
                          <div className="flex items-center gap-3 overflow-hidden min-w-0">
                            {renderGatewayLogoIcon(gw.logoIcon, isSelected, gw.id, gw.name)}
                            <div className="min-w-0">
                              <span className="font-black text-xs sm:text-sm leading-snug block truncate text-white">{gw.name}</span>
                              {gw.badgeText && (
                                <span
                                  className="text-[9px] font-extrabold px-2 py-0.5 rounded-full border inline-block mt-0.5 truncate max-w-full"
                                  style={{
                                    backgroundColor: `${gw.badgeColor || '#38bdf8'}20`,
                                    borderColor: `${gw.badgeColor || '#38bdf8'}50`,
                                    color: gw.badgeColor || '#38bdf8'
                                  }}
                                >
                                  {gw.badgeText}
                                </span>
                              )}
                            </div>
                          </div>
                          {isSelected && (
                            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-cyan-500/20 border border-cyan-400 flex items-center justify-center shrink-0">
                              <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-400" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Customer Billing Information (Required) */}
                <div className="space-y-3 pt-3 border-t border-white/10">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <label className="block text-xs font-extrabold uppercase tracking-wider text-slate-300">
                      Customer Billing Information
                    </label>
                    <span className="text-[10px] font-bold text-cyan-400">All fields mandatory *</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 mb-1">Full Name *</label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="e.g. John Doe"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-xs font-bold focus:outline-none focus:border-cyan-400 text-white placeholder:text-slate-600"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 mb-1">Email Address *</label>
                      <input
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholder="e.g. name@domain.com"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-xs font-bold focus:outline-none focus:border-cyan-400 text-white placeholder:text-slate-600"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 mb-1 flex items-center justify-between">
                        <span>
                          Phone Number * <span className="text-[10px] text-cyan-400 font-semibold">(Country Code)</span>
                        </span>
                        {customerPhone.trim().length > 3 && !phoneValidationLive.isValid && (
                          <span className="text-[10px] text-amber-400 font-medium truncate max-w-[180px]">
                            {phoneValidationLive.errorMessage || 'Invalid format'}
                          </span>
                        )}
                      </label>
                      <PhoneCountryInput
                        value={customerPhone}
                        onChange={(phone) => setCustomerPhone(phone)}
                        selectedCountry={customerCountry}
                        onCountryChange={(country) => setCustomerCountry(country)}
                        hasError={customerPhone.trim().length > 3 && !phoneValidationLive.isValid}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 mb-1">Country *</label>
                      <select
                        value={customerCountry}
                        onChange={(e) => setCustomerCountry(e.target.value)}
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-xs font-bold focus:outline-none focus:border-cyan-400 text-white cursor-pointer"
                        required
                      >
                        <optgroup label="Popular Countries">
                          {COUNTRIES_WITH_PHONE.filter((c) => c.popular).map((c) => (
                            <option key={`pop-${c.iso2}`} value={c.name}>
                              {c.flag} {c.name} ({c.dialCode})
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="All Countries">
                          {COUNTRIES_WITH_PHONE.filter((c) => !c.popular).map((c) => (
                            <option key={`all-${c.iso2}`} value={c.name}>
                              {c.flag} {c.name} ({c.dialCode})
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 mb-1">
                      Address / City * <span className="text-[10px] text-cyan-400 font-semibold">(Type for Live Recommendations)</span>
                    </label>
                    <AddressAutocompleteInput
                      value={customerAddress}
                      onChange={(addr) => setCustomerAddress(addr)}
                      selectedCountry={customerCountry}
                      placeholder="e.g. Street address, apartment, city, state"
                      required
                    />
                  </div>
                </div>

                {/* 4. SSLCOMMERZ */}
                {isSslcommerz && (
                  <div className="p-4 rounded-2xl bg-cyan-950/60 border border-cyan-500/40 text-cyan-200 text-xs space-y-2 shadow-lg h-auto transition-all duration-300">
                    <div className="flex items-center gap-2 border-b border-cyan-500/20 pb-2">
                      <Building2 className="w-4 h-4 text-cyan-400" />
                      <span className="font-extrabold text-white text-xs">SSLCommerz Digital Gateway</span>
                    </div>
                    <p className="text-[11px] text-slate-300 font-medium leading-relaxed">
                      Pay via International Debit/Credit Cards, Mobile Wallet, or Internet Banking. Verified directly through SSLCommerz secure gateway.
                    </p>
                  </div>
                )}

                {/* 5. ENTERPRISE MANUAL & BANK / CRYPTO PAYMENT FORM */}
                {isManualGateway(selectedGateway) && (() => {
                  const effectiveAddress = selectedGateway.cryptoWallet || selectedGateway.merchantAccountNumber || invoiceSettings?.cryptoDetails?.walletAddress || '0x71C7656EC7ab88b098defB751B7401B5f6d8976F';
                  const effectiveQrCode = selectedGateway.qrCodeUrl || invoiceSettings?.cryptoDetails?.qrCodeUrl || `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(effectiveAddress)}`;

                  // Accurately determine if this gateway is a Crypto wallet vs non-crypto custom gateway (e.g. Cash App, PayPal, Bank, etc.)
                  const gwNameLower = (selectedGateway.name || '').toLowerCase();
                  const isExplicitNonCrypto = gwNameLower.includes('cash app') ||
                    gwNameLower.includes('paypal') ||
                    gwNameLower.includes('bank') ||
                    gwNameLower.includes('bkash') ||
                    gwNameLower.includes('nagad') ||
                    gwNameLower.includes('rocket') ||
                    gwNameLower.includes('upay') ||
                    gwNameLower.includes('venmo') ||
                    gwNameLower.includes('zelle') ||
                    gwNameLower.includes('wire');

                  const isCryptoGateway = !isExplicitNonCrypto && Boolean(
                    (selectedGateway.cryptoNetwork && selectedGateway.cryptoNetwork.trim().length > 0) ||
                    (selectedGateway.cryptoWallet && selectedGateway.cryptoWallet.trim().length > 0) ||
                    gwNameLower.includes('crypto') ||
                    gwNameLower.includes('btc') ||
                    gwNameLower.includes('bitcoin') ||
                    gwNameLower.includes('usdt') ||
                    gwNameLower.includes('eth') ||
                    gwNameLower.includes('tron') ||
                    gwNameLower.includes('trc20') ||
                    gwNameLower.includes('erc20') ||
                    gwNameLower.includes('solana')
                  );

                  const effectiveNetwork = selectedGateway.cryptoNetwork || (isCryptoGateway ? (invoiceSettings?.cryptoDetails?.network || 'USDT / BTC / ETH') : (selectedGateway.badgeText || selectedGateway.name));
                  const barcodeTitle = selectedGateway.barcodeTitle?.trim() || (isCryptoGateway ? 'Official Crypto Wallet & Barcode' : `${selectedGateway.name} Payment Details & QR Code`);
                  const barcodeSubtitle = selectedGateway.barcodeSubtitle?.trim() || (isCryptoGateway ? 'Scan QR Code or Copy Wallet Address' : `Scan QR Code or Copy ${selectedGateway.name} Details / Link`);
                  const barcodeScanHint = isCryptoGateway
                    ? 'Scan barcode with Binance, Trust Wallet, Metamask, or Crypto App'
                    : `Scan QR code with ${selectedGateway.name} or mobile camera app`;
                  const addressBoxLabel = isCryptoGateway
                    ? `Wallet Address (${effectiveNetwork}):`
                    : `Account Details / Link (${selectedGateway.name}):`;
                  const copyButtonLabel = isCryptoGateway ? 'Copy Address' : 'Copy Details';

                  return (
                    <div className="space-y-4 pt-2 h-auto transition-all duration-300 max-w-full">
                      
                      {/* HIGH VISIBILITY PAYMENT BARCODE & QR CODE BANNER */}
                      <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-b from-slate-900 via-slate-900 to-purple-950/40 border border-purple-500/40 shadow-xl space-y-4 text-center max-w-full overflow-hidden">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-white/10 pb-2.5 text-left gap-2">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 shrink-0">
                              <Wallet className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <span className="font-extrabold text-xs text-white block truncate">{barcodeTitle}</span>
                              <span className="text-[10px] text-purple-300 font-medium block truncate">{barcodeSubtitle}</span>
                            </div>
                          </div>
                          <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-purple-900/60 text-purple-200 border border-purple-600/50 self-start sm:self-auto max-w-full truncate">
                            {effectiveNetwork}
                          </span>
                        </div>

                        {/* DISPLAYED BARCODE / QR CODE IMAGE */}
                        <div className="flex flex-col items-center justify-center py-2 space-y-2">
                          <div 
                            onClick={() => setShowQrZoom(true)}
                            className="group relative p-2 bg-white rounded-[1.25rem] border border-purple-400/30 shadow-xl shadow-purple-500/10 hover:scale-[1.02] active:scale-95 transition-all cursor-pointer inline-block max-w-full"
                            title="Click to Enlarge / Zoom QR Code"
                          >
                            <img
                              src={effectiveQrCode}
                              alt={`${selectedGateway.name} Barcode QR Code`}
                              className="w-32 h-32 sm:w-40 sm:h-40 object-contain rounded-xl max-w-full"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(effectiveAddress)}`;
                              }}
                            />
                            <div className="absolute inset-0 bg-slate-950/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 text-white text-xs font-bold">
                              <Sparkles className="w-4 h-4 text-purple-300" />
                              <span>Tap to Zoom</span>
                            </div>
                          </div>
                          <p className="text-[11px] text-slate-400 font-medium">
                            {barcodeScanHint}
                          </p>
                        </div>

                        {/* COPYABLE PAYMENT DETAILS / WALLET ADDRESS */}
                        <div className="p-3 sm:p-3.5 rounded-xl bg-slate-950/95 border border-purple-500/30 space-y-1.5 text-left max-w-full overflow-hidden">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-[10px] font-bold text-purple-300 uppercase tracking-wider">
                              {addressBoxLabel}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleCopyNumber(effectiveAddress)}
                              className="px-2.5 py-1 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer shadow-md shrink-0 self-start sm:self-auto"
                            >
                              {copiedText ? (
                                <>
                                  <Check className="w-3 h-3 text-emerald-300 shrink-0" />
                                  <span className="whitespace-nowrap">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-3 h-3 shrink-0" />
                                  <span className="whitespace-nowrap">{copyButtonLabel}</span>
                                </>
                              )}
                            </button>
                          </div>
                          <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800 font-mono text-xs font-extrabold text-cyan-300 break-all select-all max-w-full">
                            {effectiveAddress}
                          </div>
                        </div>

                        {/* MANUAL DETAILS / INSTRUCTIONS */}
                        <div className="p-3 sm:p-3.5 rounded-xl bg-slate-900/95 border border-white/5 space-y-2 text-left max-w-full">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                            {selectedGateway.merchantName && (
                              <div>
                                <span className="text-[10px] text-slate-400 block font-medium">Account / Merchant Name</span>
                                <span className="font-bold text-white break-words">{selectedGateway.merchantName}</span>
                              </div>
                            )}
                            {selectedGateway.bankName && (
                              <div>
                                <span className="text-[10px] text-slate-400 block font-medium">Bank / Vault</span>
                                <span className="font-bold text-white break-words">{selectedGateway.bankName}</span>
                              </div>
                            )}
                          </div>

                          {selectedGateway.instructions && (
                            <div className="pt-2 border-t border-white/5">
                              <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider block mb-0.5">
                                Instructions:
                              </span>
                              <p className="text-xs text-slate-300 whitespace-pre-line leading-relaxed font-medium break-words">
                                {selectedGateway.instructions}
                              </p>
                            </div>
                          )}
                        </div>

                      </div>

                      {/* MANUAL PAYMENT SUBMISSION FORM */}
                      <div className="p-3.5 sm:p-4 rounded-2xl bg-slate-900/95 border border-slate-800 space-y-3 text-left max-w-full">
                        <h4 className="font-extrabold text-xs text-white uppercase tracking-wider flex items-center gap-1.5">
                          <FileText className="w-4 h-4 text-cyan-400 shrink-0" />
                          <span>Payment Confirmation Details</span>
                        </h4>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-bold text-slate-300 mb-1">Transaction ID (TrxID) *</label>
                            <input
                              type="text"
                              required
                              value={manualTrxId}
                              onChange={(e) => setManualTrxId(e.target.value)}
                              placeholder="e.g. 9J82A10K9"
                              className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs font-mono font-bold text-cyan-300 focus:outline-none focus:border-cyan-400"
                            />
                          </div>

                          <div>
                            <label className="block text-[11px] font-bold text-slate-300 mb-1">Payment Amount *</label>
                            <input
                              type="number"
                              step="0.01"
                              required
                              value={manualAmount}
                              onChange={(e) => setManualAmount(e.target.value)}
                              className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs font-bold text-white focus:outline-none focus:border-cyan-400"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-[11px] font-bold text-slate-300">Payment Date & Time *</label>
                              <button
                                type="button"
                                onClick={() => setManualDateTime(getLocalDatetimeNow())}
                                className="text-[10px] text-cyan-400 hover:text-cyan-300 font-bold hover:underline cursor-pointer"
                              >
                                Set to Now
                              </button>
                            </div>
                            <input
                              type="datetime-local"
                              required
                              value={manualDateTime}
                              onChange={(e) => setManualDateTime(e.target.value)}
                              style={{ colorScheme: 'dark' }}
                              className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs font-semibold text-white focus:outline-none focus:border-cyan-400 max-w-full cursor-pointer"
                            />
                          </div>

                          <div>
                            <label className="block text-[11px] font-bold text-slate-300 mb-1">Sender Number / Wallet Account *</label>
                            <input
                              type="text"
                              required
                              value={manualSenderAccount}
                              onChange={(e) => setManualSenderAccount(e.target.value)}
                              placeholder="e.g. 0x... / Wallet Address"
                              className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs font-bold text-white focus:outline-none focus:border-cyan-400"
                            />
                          </div>
                        </div>

                        {/* Screenshot Upload (Optional) */}
                        <div>
                          <label className="block text-[11px] font-bold text-slate-300 mb-1">
                            Payment Screenshot / Receipt (Optional)
                          </label>
                          <div className="flex items-center gap-3">
                            <label className="flex-1 px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 hover:border-cyan-400 text-xs text-slate-300 font-bold cursor-pointer flex items-center gap-2 transition-colors min-w-0">
                              <Upload className="w-4 h-4 text-cyan-400 shrink-0" />
                              <span className="truncate">{manualScreenshotUrl ? 'Change Screenshot' : 'Upload Receipt Screenshot'}</span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleScreenshotUpload}
                                className="hidden"
                              />
                            </label>
                            {manualScreenshotUrl && (
                              <div className="relative group shrink-0">
                                <img
                                  src={manualScreenshotUrl}
                                  alt="Screenshot Preview"
                                  className="w-10 h-10 rounded-lg object-cover border border-cyan-400"
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Payment Note */}
                        <div>
                          <label className="block text-[11px] font-bold text-slate-300 mb-1">Payment Note (Optional)</label>
                          <input
                            type="text"
                            value={manualNote}
                            onChange={(e) => setManualNote(e.target.value)}
                            placeholder="Additional remarks or reference details..."
                            className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs text-white focus:outline-none focus:border-cyan-400"
                          />
                        </div>
                      </div>

                    </div>
                  );
                })()}

              </div>

              {/* Right Column: Sticky Order Summary */}
              <div className="md:col-span-5 flex flex-col justify-between space-y-6 p-6 rounded-2xl bg-slate-900/60 border border-white/10 md:sticky md:top-0 self-start">
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-white/10 pb-3">
                    <h4 className="font-extrabold text-sm uppercase text-slate-300 tracking-wider">
                      Order Summary
                    </h4>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-950 text-cyan-300 border border-cyan-800">
                      {cartItems.length} {cartItems.length === 1 ? 'Item' : 'Items'}
                    </span>
                  </div>

                  <div className="space-y-3 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                    {cartItems.map((item, idx) => (
                      <div key={`${item.product.id}-${item.selectedFormat}-${idx}`} className="flex items-center justify-between text-xs gap-2">
                        <div 
                          className="min-w-0 cursor-pointer group"
                          onClick={() => {
                            if (onNavigateProduct) {
                              onNavigateProduct(item.product);
                              onClose();
                            }
                          }}
                        >
                          <span className="font-semibold text-slate-200 truncate block group-hover:text-cyan-400 transition-colors">{item.product.title}</span>
                          <span className="text-[10px] text-slate-400 font-mono">Format: {item.selectedFormat}</span>
                        </div>
                        <span className="font-bold text-cyan-400 flex-shrink-0">{symbol}{(item.product.price * rate).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-slate-800 pt-3 space-y-2 text-xs">
                    <div className="flex justify-between text-slate-400">
                      <span>Subtotal</span>
                      <span>{symbol}{(subtotal * rate).toFixed(2)}</span>
                    </div>
                    {discountAmount > 0 && (
                      <div className="flex justify-between text-emerald-400">
                        <span>Discount ({appliedCoupon?.code})</span>
                        <span>-{symbol}{(discountAmount * rate).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-slate-400">
                      <span>Processing Fee</span>
                      {processingFeeUSD > 0 ? (
                        <span className="text-amber-400 font-bold flex items-center gap-1">
                          <span>+{symbol}{processingFeeInCurrency.toFixed(2)}</span>
                          <span className="text-[10px] text-amber-300/80 font-normal">
                            ({feePercent > 0 ? `${feePercent}%` : ''}{feePercent > 0 && feeFixed > 0 ? ' + ' : ''}{feeFixed > 0 ? `${symbol}${(feeFixed * rate).toFixed(2)}` : ''})
                          </span>
                        </span>
                      ) : (
                        <span className="text-emerald-400 font-bold">FREE ($0.00)</span>
                      )}
                    </div>
                    <div className="flex justify-between font-extrabold text-base text-white border-t border-slate-800 pt-2">
                      <span>Total</span>
                      <span className="text-cyan-400">{symbol}{totalAmountInCurrency}</span>
                    </div>
                  </div>

                  {/* Gateway Selected Badge */}
                  <div className="p-2.5 rounded-xl bg-slate-950/95 border border-slate-800 flex items-center justify-between text-[11px]">
                    <span className="text-slate-400 font-medium">Payment Gateway:</span>
                    <span className="font-extrabold text-cyan-300 flex items-center gap-1.5">
                      {renderGatewayLogoIcon(selectedGateway.logoIcon, true)}
                      <span>{selectedGateway.name}</span>
                    </span>
                  </div>
                </div>

                {!isEmailVerified ? (
                  <div className="p-3.5 rounded-2xl bg-amber-950/40 border border-amber-800/80 text-amber-300 text-xs font-semibold space-y-2">
                    <div className="flex items-center gap-2 font-bold text-amber-200">
                      <Lock className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      <span>Email Verification Required</span>
                    </div>
                    <p className="text-[11px] text-amber-200/90 leading-relaxed">
                      Please verify your email address with the 6-digit OTP code before completing your order.
                    </p>
                  </div>
                ) : null}

                <button
                  onClick={handlePay}
                  disabled={isProcessing || !isEmailVerified || (isPaypal && !isPaypalConfigured)}
                  className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 font-extrabold text-white text-xs shadow-xl shadow-cyan-500/20 hover:scale-[1.01] active:scale-[0.99] transition-transform disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {!isEmailVerified ? (
                    <>
                      <Lock className="w-4 h-4 text-amber-300" />
                      <span>Verify Email First</span>
                    </>
                  ) : isProcessing ? (
                    <>
                      <Sparkles className="w-4 h-4 animate-spin" />
                      <span>Processing Order...</span>
                    </>
                  ) : (
                    <>
                      <span>{getDynamicButtonText()}</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>

            </div>
          )}

        </div>

      </div>
    </div>
    </div>
  );
};
